/**
 * User permission helpers: the permission access map and a checker that
 * resolves whether a user group (and PO status) is allowed a permission.
 * @exports userAccess, checkUserPermission
 * @author {Cognizant Technology Solutions}
 */
import USERGROUP, { ALLOWED_USER_GROUP } from '../constants/userGroup';

// Permission access map: nested by area, each leaf holds `allowedUser` groups
// (and optional `showForStatus` for status-gated permissions).
export const userAccess = {
  Dashboard: {
    primaryDashboard: {
      allowedUser: ALLOWED_USER_GROUP
    },
    operationalmetrics: {
      allowedUser: ALLOWED_USER_GROUP
    },
    dashboard: {
      allowedUser: ALLOWED_USER_GROUP
    }
  },
  PoSearch: {
    allowedUser: ALLOWED_USER_GROUP,
    allowedSearchAction: {
      poAcceptance: {
        allowedUser: [
          USERGROUP.PMO_SECURITY,
          USERGROUP.PMO,
          USERGROUP.INFOTECH_SECURITY,
          USERGROUP.FACTORY
        ],
        showForStatus: [
          'Unissued',
          'Unaccepted',
          'Rejected'
        ]
      },
      gacInApp: {
        allowedUser: [
          USERGROUP.PMO_SECURITY,
          USERGROUP.PMO,
          USERGROUP.INFOTECH_SECURITY,
          USERGROUP.FACTORY
        ],
        showForStatus: [
          'Unissued',
          'Accepted',
          'Unaccepted',
          'Rejected'
        ]
      },
      WIP: {
        allowedUser: [
          USERGROUP.FACTORY
        ]
      },
      legalPoPdf: {
        allowedUser: ALLOWED_USER_GROUP,
        showForStatus: [
          'Accepted',
          'Closed'
        ]
      },
      fxAdjustment: {
        allowedUser: [
          USERGROUP.PMO_SECURITY,
          USERGROUP.PMO,
          USERGROUP.FACTORY
        ],
        showForStatus: [
          'Accepted'
        ]
      },
      issuePO: {
        allowedUser: [
          USERGROUP.WHQ
        ]
      },
      manufacturingCodeInApp: {
        allowedUser: [
          USERGROUP.FACTORY
        ],
        showForStatus: [
          'Unissued',
          'Accepted',
          'Unaccepted',
          'Rejected'
        ]
      }
    },
    restrictedSearchCriteriaUser: {
      DPOMLineItemStatus: {
        allowedUser: [
          USERGROUP.PMO_SECURITY,
          USERGROUP.PMO,
          USERGROUP.INFOTECH_SECURITY,
          USERGROUP.INFOTECH,
          USERGROUP.WHQ,
          USERGROUP.BPM_SECURITY,
          USERGROUP.GEONA_SECURITY,
          USERGROUP.GEOGC_SECURITY,
          USERGROUP.GEOEMEA_SECURITY,
          USERGROUP.GEOAPLA_SECURITY,
          USERGROUP.GEONA,
          USERGROUP.GEOGC,
          USERGROUP.GEOEMEA,
          USERGROUP.GEOAPLA
        ]
      }
    }
  },
  Reports: {
    ppmreport: {
      allowedUser: ALLOWED_USER_GROUP
    },
    poreport: {
      allowedUser: ALLOWED_USER_GROUP
    },
    pochangereport: {
      allowedUser: ALLOWED_USER_GROUP
    },
    lineitemreport: {
      allowedUser: ALLOWED_USER_GROUP
    },
    conversationreport: {
      allowedUser: ALLOWED_USER_GROUP
    },
    airfreightreport: {
      allowedUser: ALLOWED_USER_GROUP,
      overideEligible: {
        allowedUser: [
          USERGROUP.FACTORY
        ]
      },
      overideWorkflow: {
        allowedUser: [
          USERGROUP.PMO,
          USERGROUP.PMO_SECURITY
        ]
      },
      csvAFReportDownload:
      {
        allowedUser: [
          USERGROUP.PMO_SECURITY,
          USERGROUP.PMO,
          USERGROUP.BPM_SECURITY,
          USERGROUP.GEOAPLA_SECURITY,
          USERGROUP.GEOEMEA_SECURITY,
          USERGROUP.GEOGC_SECURITY,
          USERGROUP.GEONA_SECURITY,
          USERGROUP.WHQ,
          USERGROUP.GEOAPLA,
          USERGROUP.GEOEMEA,
          USERGROUP.GEOGC,
          USERGROUP.GEONA,
          USERGROUP.INFOTECH_SECURITY,
          USERGROUP.INFOTECH
        ]
      },
      showAFScheduler:
      {
        allowedUser: [
          USERGROUP.PMO_SECURITY,
          USERGROUP.PMO,
          USERGROUP.BPM_SECURITY,
          USERGROUP.GEOAPLA_SECURITY,
          USERGROUP.GEOEMEA_SECURITY,
          USERGROUP.GEOGC_SECURITY,
          USERGROUP.GEONA_SECURITY,
          USERGROUP.WHQ,
          USERGROUP.GEOAPLA,
          USERGROUP.GEOEMEA,
          USERGROUP.GEOGC,
          USERGROUP.GEONA,
          USERGROUP.INFOTECH_SECURITY,
          USERGROUP.INFOTECH
        ]
      }
    },
    gacchangehistoryreport: {
      allowedUser: ALLOWED_USER_GROUP
    },
    todos: {
      allowedUser: ALLOWED_USER_GROUP
    }
  },
  Conversations: {
    allowedUser: ALLOWED_USER_GROUP,
    stakeholderDelete: {
      allowedUser: [
        USERGROUP.PMO_SECURITY,
        USERGROUP.PMO,
        USERGROUP.INFOTECH_SECURITY,
        USERGROUP.INFOTECH,
        USERGROUP.WHQ,
        USERGROUP.BPM_SECURITY,
        USERGROUP.GEONA_SECURITY,
        USERGROUP.GEOGC_SECURITY,
        USERGROUP.GEOEMEA_SECURITY,
        USERGROUP.GEOAPLA_SECURITY,
        USERGROUP.GEONA,
        USERGROUP.GEOGC,
        USERGROUP.GEOEMEA,
        USERGROUP.GEOAPLA
      ]
    }
  },
  Upload: {
    uploadPOAcceptance: {
      allowedUser: [
        USERGROUP.INFOTECH_SECURITY,
        USERGROUP.PMO_SECURITY,
        USERGROUP.PMO,
        USERGROUP.FACTORY,
        USERGROUP.INFOTECH
      ]
    },
    uploadGac: {
      allowedUser: [
        USERGROUP.INFOTECH_SECURITY,
        USERGROUP.PMO_SECURITY,
        USERGROUP.PMO,
        USERGROUP.FACTORY
      ]
    },
    uploadWIP: {
      allowedUser: [
        USERGROUP.FACTORY
      ]
    },
    uploadPORejection: {
      allowedUser: [
        USERGROUP.INFOTECH_SECURITY,
        USERGROUP.PMO_SECURITY,
        USERGROUP.PMO,
        USERGROUP.FACTORY
      ]
    },
    uploadManufacturingCode: {
      allowedUser: [
        USERGROUP.FACTORY
      ]
    }
  },
  Download: {
    downloadPOPdf: {
      allowedUser: ALLOWED_USER_GROUP
    },
    downloadSireInstruction: {
      allowedUser: ALLOWED_USER_GROUP
    },
    downloadReports: {
      allowedUser: ALLOWED_USER_GROUP
    }
  },
  SireDelivery: {
    allowedUser: ALLOWED_USER_GROUP
  },
  SavedSearches: {
    allowedUser: ALLOWED_USER_GROUP
  },
  AFOverride: {
    AFApproval: {
      allowedUser: [
        USERGROUP.PMO,
        USERGROUP.PMO_SECURITY,
        USERGROUP.WHQ,
        USERGROUP.GEONA_SECURITY,
        USERGROUP.GEOGC_SECURITY,
        USERGROUP.GEOEMEA_SECURITY,
        USERGROUP.GEOAPLA_SECURITY,
        USERGROUP.GEONA,
        USERGROUP.GEOGC,
        USERGROUP.GEOEMEA,
        USERGROUP.GEOAPLA
      ]
    },
    AFOverrideAdjustment: {
      allowedUser: [
        USERGROUP.PMO_SECURITY
      ]
    }
  },
  AFAdjustments: {
    allowedUser: [
      USERGROUP.PMO,
      USERGROUP.PMO_SECURITY
    ]
  },
  Settings: {
    allowedUser: ALLOWED_USER_GROUP,
    pmodec: {
      allowedUser: [
        USERGROUP.PMO_SECURITY
      ]
    },
    geoglobal: {
      allowedUser: [
        USERGROUP.GEONA_SECURITY,
        USERGROUP.GEOGC_SECURITY,
        USERGROUP.GEOEMEA_SECURITY,
        USERGROUP.GEOAPLA_SECURITY
      ]
    },
    geoEmailGroup: {
      allowedUser: [
        USERGROUP.GEONA_SECURITY,
        USERGROUP.GEOGC_SECURITY,
        USERGROUP.GEOEMEA_SECURITY,
        USERGROUP.GEOAPLA_SECURITY
      ]
    },
    conversationcategorycodes: {
      allowedUser: [
        USERGROUP.PMO_SECURITY
      ]
    },
    gacreasoncodes: {
      // for testing PMO_SECURITY added
      allowedUser: [
        USERGROUP.BPM_SECURITY,
        USERGROUP.PMO_SECURITY,
        USERGROUP.INFOTECH
      ]
    },
    gacreasonusecases: {
      allowedUser: [
        USERGROUP.PMO_SECURITY,
        USERGROUP.INFOTECH_SECURITY
      ]
    },
    featureFlag: {
      allowedUser: [
        USERGROUP.INFOTECH_SECURITY,
        USERGROUP.INFOTECH
      ]
    }
  },
  ExternalLink: {
    allowedUser: ALLOWED_USER_GROUP
  }
};
/**
 * Recursively finds the first nested value for a key within an object.
 * @param {Object} obj The object to search
 * @param {string} key The permission key to find
 * @returns {?Object} The matched permission config, or null
 */
function findNested(obj, key) {
  if (obj[key]) {
    return obj[key];
  }
  return Object.keys(obj).reduce((result, k) => {
    if (result) {
      return result;
    }
    if (obj[k] && typeof obj[k] === 'object') {
      return findNested(obj[k], key);
    }
    return null;
  }, null);
}
/**
 * Checks whether a user group is allowed a permission, additionally enforcing
 * PO line item status for status-gated permissions.
 * @param {string} permission The permission key to check
 * @param {string|Array<string>} userGroup The user's group(s)
 * @param {string} [dpomLineItemStatus] The PO line item status for status gates
 * @returns {?boolean} True/false when found, or null when no config matches
 */
export const checkUserPermission = (permission, userGroup, dpomLineItemStatus = '') => {
  const allowedArr = findNested(userAccess, permission);

  if (Object.keys(allowedArr).includes('allowedUser')) {
    let allowedGrp = allowedArr.allowedUser.includes(userGroup);
    if (Array.isArray(userGroup)) {
      allowedGrp = USERGROUP.some((e) => allowedArr.allowedUser.includes(e));
    }
    if (['poAcceptance', 'gacInApp', 'legalPoPdf', 'fxAdjustment', 'issuePO', 'manufacturingCodeInApp'].includes(permission)) {
      return (
        allowedGrp
        && allowedArr.showForStatus.includes(dpomLineItemStatus)
      );
    }
    return allowedGrp;
  }
  return null;
};
